import React from 'react';

const images = [
  '/airbnb/a1.jpeg.avif',
  '/airbnb/a2.jpeg.avif',
  '/airbnb/a3.jpeg.webp',
  '/airbnb/a4.jpeg.avif',
  '/airbnb/a5.jpeg.avif',
  '/airbnb/a6.jpeg.avif'
];

const Airbnb: React.FC = () => {
  return (
    <div className="pt-16 sm:pt-18 lg:pt-16 min-h-screen bg-cream">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-orange-50 to-cream py-10 sm:py-12 lg:py-16">
        <div className="container-base text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-pacifico text-orange-500 mb-2 sm:mb-3">
            Stay with Us
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-xl mx-auto px-4">
            A charming, cozy getaway in the heart of Bluffton – just minutes from everything. Clean, stylish, and full of Southern charm.
          </p>
        </div>
      </div>

      {/* Gallery Section */}
      <section className="section-sm">
        <div className="container-base">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {images.map((src, index) => (
              <div
                key={index}
                className={`
                  img-frame shadow-card
                  ${index === 0 ? 'col-span-2 md:col-span-2 row-span-2' : ''}
                `}
              >
                <div className={`
                  ${index === 0 ? 'aspect-[4/3] md:aspect-square' : 'aspect-square'}
                  overflow-hidden
                `}>
                  <img
                    src={src}
                    alt={`Airbnb photo ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    loading={index > 2 ? 'lazy' : undefined}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-white">
        <div className="container-narrow">
          <div className="card-elevated p-8 sm:p-10 lg:p-12 text-center">
            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {['Cozy Retreat', 'Prime Location', 'Southern Charm'].map((feature) => (
                <span key={feature} className="badge-orange">
                  {feature}
                </span>
              ))}
            </div>

            <h2 className="text-h2 sm:text-h1 text-orange-500 mb-4">Book Direct and Save</h2>
            <p className="text-body-lg text-gray-700 mb-2">
              Contact Kristie for a discounted rate at
            </p>
            <a
              href="tel:+12625152371"
              className="inline-block text-2xl sm:text-3xl font-bold text-orange-500 hover:text-orange-600 transition-colors mb-8"
            >
              (262) 515‑2371
            </a>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.airbnb.com/rooms/719528086356605460?check_out=2024-08-18&viralityEntryPoint=1&unique_share_id=1848523C-FE32-45A4-B86E-969A24A75382&slcid=8583aa2bfac44b47bdc69e3f37c17f35&s=76&feature=share&adults=1&check_in=2024-08-15&channel=native&slug=8ZSqE5o5&source_impression_id=p3_1722806295_P3c-9PTAG2oyOT9z"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-lg"
              >
                Book Your Stay
              </a>
              <a
                href="tel:+12625152371"
                className="btn-secondary btn-lg"
              >
                Call for Discount
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="section-sm bg-cream">
        <div className="container-base">
          <h3 className="text-h3 text-center text-gray-900 mb-8">What to Expect</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                ),
                label: 'Entire Home'
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                  </svg>
                ),
                label: 'Free WiFi'
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                ),
                label: 'Spotless Clean'
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ),
                label: 'Great Location'
              }
            ].map((item, i) => (
              <div key={i} className="card p-4 text-center">
                <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-500 flex items-center justify-center mx-auto mb-3">
                  {item.icon}
                </div>
                <span className="text-body-sm font-medium text-gray-800">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Airbnb;
